/**
 * ==========================================================================
 *
 * Scope: Foundation Phase
 *
 * Responsibility:
 * Allows designers to manually assign AI Widget Roles while creating
 * reusable AI Layout Templates.
 *
 * ==========================================================================
 */

import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import cx from "classnames";

import { widgetFamilies, widgetRoles } from "../Masters/WidgetRoleMaster";
import { EditorContext } from "../../../containers/editor/EditorLayout";
import { Input } from "../../ui/input";
import { Icon } from "../../ui/icon";
import global from "../../../scss/dhp.scss";

const style = Object.assign({}, global);

const WidgetRoleDropdown = props => {
  const defaultOption = {
    key: "none",
    name: "None",
    group: "None",
  };
  const selectedRole = props.wRole || defaultOption;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");

  const toggleDropdown = () => {
    setDropdownOpen(prev => !prev);
  };

  const handleSearchChange = e => {
    setSearchKeyword(e.target.value);
  };

  const filteredGroups = useMemo(() => {
    const keyword = searchKeyword.toLowerCase();

    if (!keyword) {
      return props.wRoles;
    }

    return props.wRoles
      .map(group => ({
        ...group,
        options: group.options.filter(role => role.name.toLowerCase().includes(keyword)),
      }))
      .filter(group => group.options.length > 0);
  }, [props.wRoles, searchKeyword]);

  const selectRole = (role, group = null) => {
    const selected = {
      key: role.key,
      name: role.name,
      group: group?.group || "None",
    };

    props.setWRole(selected);
    setDropdownOpen(false);
    setSearchKeyword("");
  };

  return (
    <Dropdown className={style["custom_select2"]} isOpen={dropdownOpen} toggle={toggleDropdown}>
      <DropdownToggle className={style["w-100"]}>
        {selectedRole.name}
        <Icon icon="ui-arrow-down" />
      </DropdownToggle>

      <DropdownMenu className={cx(style["shadow-sm"], style["border-0"], style["rounded"], style["w-100"])}>
        <div className={cx(style["search"], style["px-3"], style["pt-2"])}>
          <Input
            cssModule={style}
            returnType="inputGroup"
            addonType="prepend"
            type="search"
            name="widget-role-search"
            value={searchKeyword}
            onChange={handleSearchChange}
            autoFocus
          />
        </div>

        <div className={cx(style["customScroll"], style["scroll-Y"])}>
          {/* Default Option */}
          <DropdownItem
            tag="a"
            toggle={false}
            className={cx({
              [style["active"]]: selectedRole.key === defaultOption.key,
            })}
            onClick={() => selectRole(defaultOption)}>
            {defaultOption.name}
          </DropdownItem>

          {filteredGroups.length === 0 && <div className={style["not-found"]}>No results found</div>}

          {filteredGroups.map(group => (
            <div key={group.group} className={style["mb-2"]}>
              {/* Group = NOT SELECTABLE */}
              <DropdownItem header>{group.group}</DropdownItem>

              {/* Widget Role = SELECTABLE */}
              {group.options.map(role => (
                <DropdownItem
                  tag="a"
                  key={role.key}
                  toggle={false}
                  className={cx({
                    [style["active"]]: role.key === selectedRole.key,
                  })}
                  onClick={() => selectRole(role, group)}>
                  {role.name}
                </DropdownItem>
              ))}
            </div>
          ))}
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

WidgetRoleDropdown.propTypes = {
  wRole: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    group: PropTypes.string,
  }),
  wRoles: PropTypes.array.isRequired,
  setWRole: PropTypes.func.isRequired,
};

const WidgetRoleModal = props => {
  const { metadata, widgets, updateWidgets, rightContextMenu, updateRightContextMenu } = useContext(EditorContext);
  const activeWidgetId = metadata.activeWidgetId[0];
  const activeWidgetObj = widgets?.find(widget => widget.id === activeWidgetId);

  const [wRole, setWRole] = useState(null);
  const [wRoles, setWRoles] = useState([]);

  const isFormValid = activeWidgetObj?.AI?.widget_role?.name !== wRole?.name;

  const applyHandlerChanges = enable => {
    const handlerWRIndicator = document.querySelector("#dhp-widget-handler .widget-role-indicator");
    if (!handlerWRIndicator) return;

    handlerWRIndicator.classList[enable ? "remove" : "add"]("invisible");
  };

  const closeModal = () => {
    props.setShowModal(!props.showModal);

    if (rightContextMenu.enable) {
      updateRightContextMenu({
        ...rightContextMenu,
        enable: false,
      });
    }
  };

  const getWRoles = () => {
    const widget = document.getElementById(activeWidgetId);

    if (!widget) {
      return;
    }

    const assetType = widget.dataset.assetType;
    const matchingFamilies = [];

    Object.keys(widgetFamilies).forEach(wFamily => {
      if (widgetFamilies[wFamily].includes(assetType)) {
        matchingFamilies.push(wFamily);
      }
    });

    let matchingFamily;

    if (matchingFamilies.length === 1) {
      matchingFamily = matchingFamilies[0];
    } else if (widget.querySelectorAll("video")?.length === 1) {
      matchingFamily = "video";
    } else {
      matchingFamily = "image_and_graphic";
    }

    const matchingOptions = widgetRoles[matchingFamily] || [];

    setWRoles(matchingOptions);
  };

  const getAppliedWRole = () => {
    const appliedWRole = JSON.parse(JSON.stringify(activeWidgetObj?.AI?.widget_role || null));

    if (!appliedWRole) return;

    let wRKey = null;

    wRoles.forEach(group => {
      group.options.forEach(role => {
        if (role.name === appliedWRole.name) {
          wRKey = role.key;
        }
      });
    });

    if (!wRKey) return;

    setWRole({
      ...appliedWRole,
      key: wRKey,
    });
  };

  const applyWRole = () => {
    if (!wRole) return;

    // compose widget role data
    const widgetRoleData =
      wRole.key !== "none"
        ? {
            name: wRole.name,
            group: wRole.group,
          }
        : null;

    // update widgets context
    updateWidgets(
      widgets.map(widget => {
        if (widget.id === activeWidgetId) {
          return {
            ...widget,
            AI: {
              ...widget.AI,
              widget_role: widgetRoleData,
            },
          };
        } else {
          return widget;
        }
      })
    );

    // apply handler level UI changes
    applyHandlerChanges(Boolean(widgetRoleData));

    // close modal and right context menu
    closeModal();
  };

  useEffect(() => {
    if (wRoles.length > 0) {
      getAppliedWRole();
    } else {
      getWRoles();
    }
  }, [wRoles]);

  return (
    <>
      <h4 className={cx(style["fw-7"], style["mb-4"])}>Widget Role</h4>

      <span className={cx(style["cross-modal"], style["rounded"])} onClick={closeModal}>
        <Icon icon="ui-close" />
      </span>

      <div className={cx(style["form-group"], style["mb-4"])}>
        <h6 className={cx(style["fw-7"])}>Role</h6>

        <WidgetRoleDropdown wRole={wRole} wRoles={wRoles} setWRole={setWRole} />
      </div>

      <Button type="button" color="primary" disabled={!isFormValid} onClick={applyWRole}>
        Apply
      </Button>
    </>
  );
};

WidgetRoleModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
};

export default WidgetRoleModal;
